import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { RefreshCcw, Rocket, Search } from 'lucide-react';
import { deploymentDashboardApi } from '../../services/deploymentApi';
import {
  DeploymentCurrentRecord,
  DeploymentCustomer,
  DeploymentRecord,
  DeploymentServer,
  DeploymentServerGroup,
  DeploymentTargetRecord,
  RepositoryCatalogRecord,
} from '../../types/deployment';
import { ServerDeploymentGroup } from './ServerDeploymentGroup';
import { DeploymentHistoryState } from './DeploymentHistoryPanel';
import {
  getDeploymentActivityAt,
  matchesDeploymentSearch,
} from './deploymentExplorerUtils';

const SERVER_GROUP_PAGE_LIMIT = 100;
const CURRENT_ROWS_PER_GROUP = 50;
const HISTORY_PAGE_SIZE = 10;

type DeploymentExplorerFilters = {
  customer: string;
  server: string;
  repository: string;
  environment: string;
  status: string;
  actionType: string;
  search: string;
};

const initialFilters: DeploymentExplorerFilters = {
  customer: '',
  server: '',
  repository: '',
  environment: '',
  status: '',
  actionType: '',
  search: '',
};

interface DeploymentsExplorerProps {
  customers: DeploymentCustomer[];
  servers: DeploymentServer[];
  repositories: RepositoryCatalogRecord[];
  targets: DeploymentTargetRecord[];
  refreshKey: number;
  saving?: boolean;
  onOpenDeployDialog: () => void;
  onCacheDeployments: (deployments: DeploymentRecord[]) => void;
  onOpenDeploymentDetails: (deploymentId: string) => void;
  onRefreshDeployment: (deploymentId: string) => void;
  onComposeStart: (deploymentId: string) => void;
  onComposeStop: (deploymentRecord: DeploymentRecord) => void;
  onOpenRestart: (deploymentRecord: DeploymentRecord) => void;
  onOpenRollback: (deploymentRecord: DeploymentRecord) => void;
}

const getRequestFailureMessage = (error: unknown, fallbackMessage: string) => {
  const err = error as {
    message?: string;
    response?: {
      data?: {
        message?: string;
      };
    };
  };

  if (err?.response?.data?.message) {
    return err.response.data.message;
  }

  if (err?.message) {
    return err.message;
  }

  return fallbackMessage;
};

const unsettledStatuses = new Set<DeploymentRecord['status']>([
  'requested',
  'pipeline_created',
  'manual_job_pending',
  'running',
]);

export const DeploymentsExplorer: React.FC<DeploymentsExplorerProps> = ({
  customers,
  servers,
  repositories,
  targets,
  refreshKey,
  saving = false,
  onOpenDeployDialog,
  onCacheDeployments,
  onOpenDeploymentDetails,
  onRefreshDeployment,
  onComposeStart,
  onComposeStop,
  onOpenRestart,
  onOpenRollback,
}) => {
  const [filters, setFilters] = useState<DeploymentExplorerFilters>(initialFilters);
  const [serverGroups, setServerGroups] = useState<DeploymentServerGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [warning, setWarning] = useState('');
  const [expandedServerIds, setExpandedServerIds] = useState<Record<string, boolean>>({});
  const [historyBySlice, setHistoryBySlice] = useState<Record<string, DeploymentHistoryState>>({});
  const [openHistoryBySlice, setOpenHistoryBySlice] = useState<Record<string, boolean>>({});

  const environmentOptions = useMemo(() => (
    Array.from(new Set(targets.map((target) => String(target.environment || '').trim()).filter(Boolean))).sort()
  ), [targets]);

  const loadServerGroups = useCallback(async (refresh = false) => {
    setLoading(true);
    setError('');

    try {
      const response = await deploymentDashboardApi.getDeploymentServerGroups({
        customer: filters.customer || undefined,
        server: filters.server || undefined,
        repository: filters.repository || undefined,
        environment: filters.environment || undefined,
        limit: SERVER_GROUP_PAGE_LIMIT,
        currentLimit: CURRENT_ROWS_PER_GROUP,
        refresh,
      });

      setServerGroups(response.data);
      setWarning('');
      onCacheDeployments(response.data.flatMap((group) => group.currentDeployments));
      setExpandedServerIds((current) => {
        if (Object.keys(current).length > 0 || response.data.length === 0) {
          return current;
        }

        return { [response.data[0].serverId]: true };
      });
    } catch (err: unknown) {
      const message = getRequestFailureMessage(err, 'Failed to load deployment explorer');
      setError(message);
      if (refresh) {
        setWarning(`Automatic deployment status refresh failed: ${message}.`);
      }
    } finally {
      setLoading(false);
    }
  }, [filters.customer, filters.environment, filters.repository, filters.server, onCacheDeployments]);

  const loadHistory = useCallback(async (deploymentRecord: DeploymentCurrentRecord, append = false) => {
    const sliceKey = deploymentRecord.sliceKey;
    const existingState = historyBySlice[sliceKey];

    setHistoryBySlice((current) => ({
      ...current,
      [sliceKey]: {
        items: append ? existingState?.items || [] : [],
        total: existingState?.total || 0,
        loaded: append ? Boolean(existingState?.loaded) : false,
        error: '',
        loading: true,
      },
    }));

    try {
      const deploymentTargetId = (
        typeof deploymentRecord.deploymentTarget === 'string'
          ? deploymentRecord.deploymentTarget
          : deploymentRecord.deploymentTarget?._id
      ) || '';
      const repositoryId = (
        typeof deploymentRecord.repository === 'string'
          ? deploymentRecord.repository
          : deploymentRecord.repository?._id
      ) || '';
      const serverId = (
        typeof deploymentRecord.server === 'string'
          ? deploymentRecord.server
          : deploymentRecord.server?._id
      ) || '';

      const response = await deploymentDashboardApi.getDeploymentHistory({
        deploymentTarget: deploymentTargetId || undefined,
        repository: deploymentTargetId ? undefined : repositoryId || undefined,
        server: deploymentTargetId ? undefined : serverId || undefined,
        environment: deploymentTargetId ? undefined : deploymentRecord.environment || undefined,
        supersededOnly: true,
        limit: HISTORY_PAGE_SIZE,
        skip: append ? existingState?.items.length || 0 : 0,
      });

      onCacheDeployments(response.data);
      setHistoryBySlice((current) => ({
        ...current,
        [sliceKey]: {
          items: append ? [...(existingState?.items || []), ...response.data] : response.data,
          total: response.total,
          loaded: true,
          loading: false,
          error: '',
        },
      }));
    } catch (err: unknown) {
      setHistoryBySlice((current) => ({
        ...current,
        [sliceKey]: {
          items: append ? existingState?.items || [] : [],
          total: existingState?.total || 0,
          loaded: Boolean(existingState?.loaded),
          loading: false,
          error: getRequestFailureMessage(err, 'Failed to load deployment history'),
        },
      }));
    }
  }, [historyBySlice, onCacheDeployments]);

  useEffect(() => {
    setHistoryBySlice({});
    setOpenHistoryBySlice({});
    void loadServerGroups(true);
  }, [filters.customer, filters.environment, filters.repository, filters.server, refreshKey, loadServerGroups]);

  const currentDeployments = useMemo(() => (
    serverGroups.flatMap((group) => group.currentDeployments)
  ), [serverGroups]);

  useEffect(() => {
    if (!currentDeployments.some((deploymentRecord) => unsettledStatuses.has(deploymentRecord.status))) {
      return undefined;
    }

    const timerId = window.setInterval(() => {
      void loadServerGroups(true);
    }, 15000);

    return () => {
      window.clearInterval(timerId);
    };
  }, [currentDeployments, loadServerGroups]);

  const visibleServerGroups = useMemo(() => {
    return serverGroups
      .map((group) => ({
        ...group,
        currentDeployments: group.currentDeployments.filter((deploymentRecord) => {
          if (filters.status && deploymentRecord.status !== filters.status) {
            return false;
          }

          if (filters.actionType && deploymentRecord.actionType !== filters.actionType) {
            return false;
          }

          return matchesDeploymentSearch(deploymentRecord, filters.search);
        }),
      }))
      .filter((group) => group.currentDeployments.length > 0);
  }, [filters.actionType, filters.search, filters.status, serverGroups]);

  const explorerSummary = useMemo(() => {
    const deployments = visibleServerGroups.flatMap((group) => group.currentDeployments);
    const failed = deployments.filter((deploymentRecord) => deploymentRecord.status === 'failed').length;
    const active = deployments.filter((deploymentRecord) => unsettledStatuses.has(deploymentRecord.status)).length;
    const historical = visibleServerGroups.reduce(
      (total, group) => total + group.historicalDeploymentCount,
      0
    );

    return {
      serverCount: visibleServerGroups.length,
      deploymentCount: deployments.length,
      failed,
      active,
      historical,
    };
  }, [visibleServerGroups]);

  const handleToggleServerGroup = useCallback((serverId: string) => {
    setExpandedServerIds((current) => ({
      ...current,
      [serverId]: !current[serverId],
    }));
  }, []);

  const handleToggleHistory = useCallback((deploymentRecord: DeploymentCurrentRecord) => {
    const nextOpen = !openHistoryBySlice[deploymentRecord.sliceKey];
    setOpenHistoryBySlice((current) => ({
      ...current,
      [deploymentRecord.sliceKey]: nextOpen,
    }));

    if (nextOpen && !historyBySlice[deploymentRecord.sliceKey]?.loaded) {
      void loadHistory(deploymentRecord, false);
    }
  }, [historyBySlice, loadHistory, openHistoryBySlice]);

  const handleFilterChange = <K extends keyof DeploymentExplorerFilters>(key: K, value: DeploymentExplorerFilters[K]) => {
    setFilters((current) => ({
      ...current,
      [key]: value,
    }));
  };

  const topRecentActivity = useMemo(() => {
    const newest = currentDeployments
      .map((deploymentRecord) => getDeploymentActivityAt(deploymentRecord))
      .filter(Boolean)
      .sort((left, right) => new Date(right).getTime() - new Date(left).getTime())[0];

    return newest || '';
  }, [currentDeployments]);

  return (
    <Stack spacing={3}>
      {warning && (
        <Alert severity="warning">
          {warning}
        </Alert>
      )}
      {error && (
        <Alert severity="error">
          {error}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Stack direction={{ xs: 'column', lg: 'row' }} justifyContent="space-between" spacing={2}>
            <Box>
              <Typography variant="h5" fontWeight={700}>Server-First Deployment Explorer</Typography>
              <Typography variant="body2" color="text.secondary">
                See the latest deployment state per server first, then expand into superseded history only when you need it.
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
                <Chip size="small" variant="outlined" label={`${explorerSummary.serverCount} servers`} />
                <Chip size="small" variant="outlined" label={`${explorerSummary.deploymentCount} current slices`} />
                {explorerSummary.active > 0 ? <Chip size="small" color="info" label={`${explorerSummary.active} active`} /> : null}
                {explorerSummary.failed > 0 ? <Chip size="small" color="error" label={`${explorerSummary.failed} failed`} /> : null}
                <Chip size="small" variant="outlined" label={`${explorerSummary.historical} historical`} />
                {topRecentActivity ? (
                  <Chip size="small" variant="outlined" label={`Last activity ${new Date(topRecentActivity).toLocaleString()}`} />
                ) : null}
              </Stack>
            </Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <Button
                variant="outlined"
                startIcon={loading ? <CircularProgress size={16} /> : <RefreshCcw size={16} />}
                onClick={() => { void loadServerGroups(true); }}
                disabled={loading || saving}
              >
                Poll Status
              </Button>
              <Button
                variant="contained"
                startIcon={<Rocket size={16} />}
                onClick={onOpenDeployDialog}
                disabled={saving}
              >
                New Deployment
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6} lg={3}>
              <TextField
                fullWidth
                label="Search current deployments"
                value={filters.search}
                onChange={(event) => handleFilterChange('search', event.target.value)}
                InputProps={{
                  startAdornment: <Search size={16} style={{ marginRight: 8 }} />,
                }}
              />
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <TextField
                select
                fullWidth
                label="Customer"
                value={filters.customer}
                onChange={(event) => handleFilterChange('customer', event.target.value)}
              >
                <MenuItem value="">All customers</MenuItem>
                {customers.map((customer) => (
                  <MenuItem key={customer._id} value={customer._id}>
                    {customer.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <TextField
                select
                fullWidth
                label="Server"
                value={filters.server}
                onChange={(event) => handleFilterChange('server', event.target.value)}
              >
                <MenuItem value="">All servers</MenuItem>
                {servers.map((server) => (
                  <MenuItem key={server._id} value={server._id}>
                    {server.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <TextField
                select
                fullWidth
                label="Repository"
                value={filters.repository}
                onChange={(event) => handleFilterChange('repository', event.target.value)}
              >
                <MenuItem value="">All repositories</MenuItem>
                {repositories.map((repository) => (
                  <MenuItem key={repository._id} value={repository._id}>
                    {repository.pathWithNamespace || repository.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <TextField
                select
                fullWidth
                label="Environment"
                value={filters.environment}
                onChange={(event) => handleFilterChange('environment', event.target.value)}
              >
                <MenuItem value="">All environments</MenuItem>
                {environmentOptions.map((environment) => (
                  <MenuItem key={environment} value={environment}>
                    {environment}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <TextField
                select
                fullWidth
                label="Current status"
                value={filters.status}
                onChange={(event) => handleFilterChange('status', event.target.value)}
              >
                <MenuItem value="">All statuses</MenuItem>
                {['requested', 'pipeline_created', 'manual_job_pending', 'running', 'success', 'failed', 'cancelled'].map((status) => (
                  <MenuItem key={status} value={status}>
                    {status}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <TextField
                select
                fullWidth
                label="Current action"
                value={filters.actionType}
                onChange={(event) => handleFilterChange('actionType', event.target.value)}
              >
                <MenuItem value="">All actions</MenuItem>
                {['deploy', 'restart', 'compose-start', 'compose-stop', 'rollback'].map((actionType) => (
                  <MenuItem key={actionType} value={actionType}>
                    {actionType}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <Stack direction="row" spacing={1} sx={{ height: '100%', alignItems: 'center' }}>
                <Button
                  variant="text"
                  onClick={() => setFilters(initialFilters)}
                >
                  Reset filters
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {loading && serverGroups.length === 0 ? (
        <Stack alignItems="center" justifyContent="center" sx={{ py: 6 }}>
          <CircularProgress size={30} />
        </Stack>
      ) : null}

      {!loading && visibleServerGroups.length === 0 ? (
        <Card>
          <CardContent>
            <Typography variant="body1" fontWeight={600}>
              No deployments match the current filters.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Try clearing some filters or launch a new deployment to seed this view.
            </Typography>
          </CardContent>
        </Card>
      ) : null}

      <Stack spacing={2}>
        {visibleServerGroups.map((group) => (
          <ServerDeploymentGroup
            key={group.serverId}
            group={group}
            expanded={Boolean(expandedServerIds[group.serverId])}
            targets={targets}
            saving={saving}
            historyStateBySlice={historyBySlice}
            openHistoryBySlice={openHistoryBySlice}
            onToggleGroup={() => handleToggleServerGroup(group.serverId)}
            onToggleHistory={handleToggleHistory}
            onLoadMoreHistory={(deploymentRecord) => { void loadHistory(deploymentRecord, true); }}
            onRefreshHistory={(deploymentRecord) => { void loadHistory(deploymentRecord, false); }}
            onOpenDeploymentDetails={onOpenDeploymentDetails}
            onRefreshDeployment={onRefreshDeployment}
            onComposeStart={onComposeStart}
            onComposeStop={onComposeStop}
            onOpenRestart={onOpenRestart}
            onOpenRollback={onOpenRollback}
          />
        ))}
      </Stack>
    </Stack>
  );
};
